<template>
    <div v-show="range && section" @keydown.escape="clear">
        <focus-trap :active="range && section"
            :initial-focus="() => $refs.highlight"
            :clickOutsideDeactivates="true"
            ref="focusTrap"
        >
            <div id="popup-container"
                aria-labelledby="modal-heading"
                role="dialog"
                class="w-72"
                :class="{ 'pt-4': position == 'bottom' }"
            >
                <div v-if="position == 'bottom'" data-popper-arrow class="arrow w-3 -mt-3">
                    <svg class="fill-current text-gray-900 transform rotate-90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 35">
                        <path d="M0,17.5,24,0V35Z" />
                    </svg>
                </div>

                <div class="popup flex justify-center">
                    <div v-if="showComment" class="w-full flex flex-col items-center bg-gray-900 text-white rounded shadow-md mb-3 px-2">
                        <div class="w-full flex justify-between py-1">
                            <button class="w-5 text-lg -mt-1 text-red-500 hover:text-white cursor-pointer p-0 bg-transparent hover:bg-transparent"
                                @click="clearComment"
                            >
                                <i class="text-lg relative material-icons" style="top: 4px;">cancel</i>
                            </button>

                            <h1 id="modal-heading"
                                class="text-gray-400 text-xxs uppercase pt-1"
                            >
                                Adding Comment
                            </h1>

                            <div class="w-4">
                            </div>
                        </div>

                        <textarea class="bg-gray-100 border-2 focus:border-indigo-400 h-24 outline-none px-2 py-1 resize-none rounded text-black text-sm leading-snug w-full"
                            aria-label="Add comment"
                            ref="textarea"
                            v-model="comment"
                        />
                        <div class="w-full flex justify-between py-2">
                            <button class="text-indigo-300 text-xxs uppercase px-3 py-2 rounded-sm inline-block bg-gray-900 hover:text-red-800 hover:bg-red-200 cursor-pointer"
                                @click="clearComment"
                            >
                                No Comment
                            </button>
                            <button class="text-indigo-800 text-xxs uppercase px-4 py-2 rounded-sm inline-block bg-gray-200 hover:text-indigo-800 hover:bg-indigo-300 cursor-pointer"
                                :class="comment ? 'bg-gray-200 hover:text-indigo-900 hover:bg-indigo-300 cursor-pointer' : 'bg-gray-500'"
                                @click="saveComment"
                            >
                                Post Comment
                            </button>
                        </div>
                    </div>

                    <div v-else
                        class="bg-gray-900 text-white rounded shadow-md mb-3 px-2"
                    >
                        <h1 class="text-xxs text-center font-semibold mt-1 -mb-2 uppercase h-3 p-0"
                            :class="hoverClass"
                        >
                            {{ hovered }}
                        </h1>

                        <ul class="list-none flex justify-center w-full pt-3">
                            <li class="mb-0">
                                <button
                                    class="popup-button py-0 bg-transparent hover:bg-transparent cursor-pointer hover:text-yellow-400 px-2"
                                    @click="createAnnotation('highlight')"
                                    @mouseover="hoverOn({ text: 'highlight', color: 'text-yellow-400' })"
                                    @mouseleave="hoverOff"
                                    ref="highlight"
                                    aria-label="Add highlight"
                                >
                                    <i aria-hidden="true" class="material-icons">highlight</i>
                                </button>
                            </li>

                            <li class="mb-0">
                                <button
                                    class="popup-button py-0 bg-transparent hover:bg-transparent cursor-pointer hover:text-red-400 mt-1 px-2"
                                    @click="createAnnotation('strikethrough')"
                                    @mouseover="hoverOn({ text: 'strike', color: 'text-red-400' })"
                                    @mouseleave="hoverOff"
                                    ref="strikethrough"
                                    aria-label="Add strikethrough"
                                >
                                    <i aria-hidden="true" class="material-icons">strikethrough_s</i>
                                </button>
                            </li>

                            <li class="mb-0">
                                <button
                                    class="popup-button py-0 bg-transparent hover:bg-transparent cursor-pointer hover:text-green-400 mt-1 px-2"
                                    @click="createAnnotation('task')"
                                    @mouseover="hoverOn({ text: 'task', color: 'text-green-400' })"
                                    @mouseleave="hoverOff"
                                    ref="task"
                                    aria-label="Add task"
                                >
                                    <i aria-hidden="true" class="material-icons">add_task</i>
                                </button>
                            </li>

                            <li class="mb-0">
                                <button
                                    class="popup-button py-0 bg-transparent hover:bg-transparent cursor-pointer hover:text-indigo-400 mt-1 px-2"
                                    @click="createAnnotation('comment')"
                                    @mouseover="hoverOn({ text: 'comment', color: 'text-indigo-400' })"
                                    @mouseleave="hoverOff"
                                    ref="comment"
                                    aria-label="Add comment"
                                >
                                    <i aria-hidden="true" class="material-icons">add_comment</i>
                                </button>
                            </li>

                            <div v-if="enable_private_annotations" class="border-r border-gray-600 my-1 px-1"></div>

                            <li v-if="enable_private_annotations" class="mb-0">
                                <button
                                    class="popup-button py-0 bg-transparent hover:bg-transparent cursor-pointer hover:text-orange-500 mt-1 pl-2"
                                    @click="createAnnotation('private')"
                                    @mouseover="hoverOn({ text: 'private', color: 'text-orange-500' })"
                                    @mouseleave="hoverOff"
                                    ref="private"
                                    aria-label="Add private annotation"
                                >
                                    <i aria-hidden="true" class="material-icons">lock</i>
                                </button>
                            </li>

                            <li v-if="enable_private_annotations" class="mb-0">
                                <button
                                    class="popup-button py-0 bg-transparent hover:bg-transparent cursor-pointer hover:text-teal-500 mt-1 px-2"
                                    @click="createAnnotation('journal')"
                                    @mouseover="hoverOn({ text: 'developmental journal', color: 'text-teal-500' })"
                                    @mouseleave="hoverOff"
                                    ref="journal"
                                    aria-label="Add journal"
                                >
                                    <i aria-hidden="true" class="material-icons">phonelink_lock</i>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div v-if="position != 'bottom'" data-popper-arrow class="arrow w-3 -mt-4">
                    <svg class="fill-current text-gray-900 transform -rotate-90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 35">
                        <path d="M0,17.5,24,0V35Z" />
                    </svg>
                </div>
            </div>
        </focus-trap>
    </div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import axios from 'axios';

export default {
    props: {
        range: {},
        section: {},
        current_annotation: {},
        position: {},
        enable_private_annotations: { type: Boolean, default: false },
    },

    data() {
        return {
            instance: null,
            annotationId: null,
            showComment: false,
            comment: '',
            hovered: 'Annotate',
            hoverClass: 'text-gray-600',
        }
    },

    methods: {
        clear() {
            this.ariaUnhideReport();

            try {
                this.$refs.focusTrap?.deactivate()
            } catch {
                //
            }

            if (this.instance) {
                this.instance.destroy();
                this.instance = null;
            }
        },
        show() {
            this.ariaHideReport();

            const selectionRectangle = {
                getBoundingClientRect: () => {
                    if (this.range) {
                        let rect = this.range.getBoundingClientRect();

                        return (typeof rect !== 'undefined')
                            ? rect
                            : new DOMRect;
                    } else {
                        // prevent range unefined error in popper.js
                        return new DOMRect;
                    }
                },
            };

            this.instance = createPopper(
                selectionRectangle,
                document.querySelector('#popup-container'),
                {
                    placement: this.position == 'bottom' ? 'bottom' : 'top',
                    removeOnDestroy: true,
                    modifiers: [
                        {
                            name: 'flip',
                            enabled: false,
                        },
                        {
                            name: 'eventListeners',
                            options: {
                                scroll: false,
                                resize: false,
                            },
                        },
                    ],
                }
            );
        },
        ariaHideReport() {
            let report = document.getElementById('report');

            if (report) {
                report.ariaHidden = true;
            }
        },
        ariaUnhideReport() {
            let report = document.getElementById('report');

            if (report) {
                report.ariaHidden = false;
            }
        },
        createAnnotation(type) {
            this.$emit('create', {
                type: type,
                created: this.created,
            });
        },
        saveComment() {
            if (! this.comment || ! this.current_annotation) {
                return;
            }

            axios.patch('/users/' + this.current_annotation.report_user_id
                + '/reportsections/' + this.current_annotation.report_section_id
                + '/annotations/' + this.current_annotation.id,
                { comment: this.comment }
            ).then(response => {
                this.$emit('updateComments', { id: this.annotationId, comments: response.data.comments });
            }).catch(error => {
                if (error.response.data.message) {
                    alert('Error: ' + error.response.data.message);
                }

                console.log(error.response.data);
            }).finally(() => {
                this.clearComment();
            });
        },
        clearComment() {
            this.annotationId = null;
            this.showComment = false;
            this.comment = '';
            this.clear();
        },
        created(id) {
            this.annotationId = id;
            this.showComment = true;
            this.comment = '';
            this.$nextTick(() => {
                this.$refs.textarea.focus();
            });
        },
        hoverOn(icon) {
            this.hovered = icon.text;
            this.hoverClass = icon.color;
        },
        hoverOff() {
            this.hovered = 'Annotate';
            this.hoverClass = 'text-gray-600';
        },
    },

    created() {
        window.addEventListener('resize', this.clear);
    },

    watch: {
        range(val, oldVal) {
            this.annotationId = null;
            this.showComment = false;

            if (val && this.section && val.toString().trim() && ! val.collapsed) {
                this.show();
            } else {
                this.clear();
            }
        },
    },
}
</script>

<style>
#popup-container {
    position: absolute;
    top: -999px;
    left: -999px;
    z-index: 100;
}
</style>

